import { appPaths } from "../../shared/routing/appPaths";
import { HELP_START_PAGE, type HelpPageId } from "./content/helpPages";

/**
 * The help articles the app links to (web-app.md WA-6): each a page of the help center and
 * optionally one of its sections. Which page shows which article is a route's `handle`
 * (`app/appRoutes.tsx`); a form links its own with `HelpArticleLink`.
 */
export type HelpArticleId =
  | "threads"
  | "workflows"
  | "batches"
  | "documents"
  | "localImport"
  | "importCorpus"
  | "ontology"
  | "reviews"
  | "data"
  | "memories"
  | "projects"
  | "projectSettings"
  | "profile"
  | "preferences"
  | "connectors"
  | "agents"
  | "githubConnection"
  | "privateRepository"
  | "administration"
  | "serverKeys"
  | "executableItems";

/** A place in the help: a page, and a section of it or its top. */
export interface HelpPlace {
  readonly page: HelpPageId;
  readonly section: string | null;
}

export const HELP_ARTICLES: Readonly<Record<HelpArticleId, HelpPlace>> = {
  threads: { page: "threads", section: null },
  workflows: { page: "workflows", section: null },
  // The batch page is archived for now (archive/README.md); Lotta's section explains batches.
  batches: { page: "workflows", section: "batches" },
  documents: { page: "documents", section: null },
  localImport: { page: "import", section: "folder" },
  importCorpus: { page: "import", section: null },
  ontology: { page: "data-model", section: null },
  reviews: { page: "reviews", section: null },
  data: { page: "data", section: null },
  memories: { page: "memories", section: null },
  projects: { page: "projects", section: null },
  projectSettings: { page: "project-settings", section: null },
  profile: { page: "profile", section: null },
  preferences: { page: "profile", section: "preferences" },
  connectors: { page: "connectors", section: null },
  agents: { page: "agents", section: null },
  githubConnection: { page: "github", section: null },
  privateRepository: { page: "github", section: "private-repository" },
  administration: { page: "administration", section: null },
  serverKeys: { page: "server-keys", section: null },
  executableItems: { page: "glossary", section: "tool" },
};

export const HELP_START: HelpPlace = { page: HELP_START_PAGE, section: null };
export const HELP_GLOSSARY: HelpPlace = { page: "glossary", section: null };

export function helpPlaceHref({ page, section }: HelpPlace): string {
  return appPaths.help(page, section);
}

/** A route's `handle`: the help article of its page. */
export interface HelpHandle {
  readonly help: HelpArticleId;
}

export function helpHandle(help: HelpArticleId): HelpHandle {
  return { help };
}

/** The article of the deepest matched route that names one, or `null`. */
export function helpArticleOfMatches(matches: readonly { readonly handle: unknown }[]): HelpArticleId | null {
  for (const match of [...matches].reverse()) {
    const handle = match.handle;
    if (typeof handle === "object" && handle !== null && "help" in handle) {
      return (handle as HelpHandle).help;
    }
  }
  return null;
}
